import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { isMobile } from 'helpers';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import Stack from '@mui/material/Stack';
import ButtonBase from '@mui/material/ButtonBase';
import EditIcon from 'components/Icons/Edit';
import SquareAvatarIcon from 'components/SquareAvatarIcon';

const Wrapper = styled('div')`
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  justify-content: space-between;

  width: 100%;

  border-bottom: 1px solid #eee;
  transition: border-bottom-color 200ms;
  ${({ disableBorderBottom }) => disableBorderBottom && 'border-bottom-color: transparent;'}

  padding: ${isMobile ? '0 1rem' : '0 1.5rem'};
  min-height: 4.5rem;
`;

const Left = styled('div')`
  padding: 1rem 0;

  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Right = styled('div')`
  padding: ${({ disablePadding }) => disablePadding ? '0rem' : '1rem 0'};
  display: flex;
  align-items: center;
`;

const Title = styled('h3')`
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  overflow: hidden;
  word-break: break-word;
  flex-shrink: 0;
`;

const IconSlot = styled('span')`
  display: flex;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    .MuiAvatar-root {
      width: 2rem;
      height: 2rem;
    }

    .MuiAvatar-root svg {
      font-size: 1.25rem;
    }
  }
`;

const IconButtonBase = styled(ButtonBase)`
  position: relative;
  border-radius: 0.5rem;

  &:hover, &.Mui-focusVisible {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.palette.primary.main};
  }
`;

const EditBadge = styled('span')`
  position: absolute;
  right: -0.35rem;
  bottom: -0.35rem;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.palette.background.paper};
  color: ${({ theme }) => theme.palette.text.secondary};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  opacity: 0;
  transition: opacity 150ms;

  ${IconButtonBase}:hover &, ${IconButtonBase}.Mui-focusVisible & {
    opacity: 1;
  }

  svg {
    font-size: 0.75rem;
  }
`;

const Subtitle = styled('div')`
  font-size: 0.9rem;
  color: #888;
`;

const PaperTitle = ({ title, subtitle, children, onBackClick, disableRightPadding, Icon, iconColor, onIconClick, iconLabel, disableBorderBottom, ...rest }) => {
  const avatar = Icon && <SquareAvatarIcon Icon={Icon} variant="rounded" color={iconColor} />;

  return (
    <Wrapper disableBorderBottom={disableBorderBottom} {...rest}>
      <Left>
        <Stack direction="row" alignItems="center" spacing={1}>
          {onBackClick && (<IconButton edge="start" onClick={onBackClick}><ArrowBackIosRoundedIcon fontSize="small" /></IconButton>)}
          <Stack direction="row" alignItems="center" spacing={2}>
            {avatar && (
              <IconSlot>
                {onIconClick
                  ? (
                    <IconButtonBase onClick={onIconClick} aria-label={iconLabel} title={iconLabel}>
                      {avatar}
                      <EditBadge><EditIcon /></EditBadge>
                    </IconButtonBase>
                  )
                  : avatar}
              </IconSlot>
            )}
            <Stack direction="column" spacing={0.1}>
              <Title>{title}</Title>
              {subtitle && <Subtitle>{subtitle}</Subtitle>}
            </Stack>
          </Stack>
        </Stack>
      </Left>
      <Right disablePadding={disableRightPadding}>
        {children}
      </Right>
    </Wrapper>
  );
};

PaperTitle.propTypes = {
  disableBorderBottom: PropTypes.bool,

  onBackClick: PropTypes.func,
  title: PropTypes.any,
  subtitle: PropTypes.any,

  Icon: PropTypes.elementType,
  iconColor: PropTypes.string,
  onIconClick: PropTypes.func,
  iconLabel: PropTypes.string,

  disableRightPadding: PropTypes.bool,
};

export default PaperTitle;
